import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Empty,
  List,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
  theme,
} from 'antd';
import {
  DeleteOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  InboxOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  deleteCertificacionAttachment,
  getCertificacionAttachmentUrl,
  listCertificacionAttachments,
} from 'api/certificacionAttachment';
import { useCertificacionAttachmentUpload } from 'hooks/useCertificacionAttachmentUpload';
import useTenantFlags from 'hooks/useTenantFlags';

const { Text } = Typography;
const { useToken } = theme;
const { Dragger } = Upload;

// MIMEs aceptados por el backend (ver CERT_ATTACHMENT_ALLOWED_MIMES).
const ALLOWED_MIMES = ['application/pdf', 'image/jpeg', 'image/png'];
const ALLOWED_LABEL = 'PDF, JPG o PNG';
const MAX_SIZE_MB = 25;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

const formatSize = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const iconFor = (contentType, color) => {
  if (contentType?.startsWith('image/')) {
    return <FileImageOutlined style={{ fontSize: 24, color }} />;
  }
  return <FilePdfOutlined style={{ fontSize: 24, color }} />;
};

// Muestra los adjuntos de una certificación y permite subir/ver/borrar.
// El upload va a S3 directo via presigned PUT — este componente solo orquesta
// la UI + valida MIME/tamaño antes de disparar el flow.
function CertificacionAttachmentsSection({ certificacionId }) {
  const { token } = useToken();
  const { readOnly, minvuEnabled } = useTenantFlags();
  const { upload, loading: uploading } = useCertificacionAttachmentUpload();
  const [attachments, setAttachments] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const refresh = useCallback(async () => {
    if (!certificacionId) return;
    setLoadingList(true);
    setLoadError(null);
    try {
      const rows = await listCertificacionAttachments(certificacionId);
      setAttachments(rows);
    } catch (err) {
      setLoadError(err?.response?.data?.message || err?.message || 'Error cargando adjuntos');
    } finally {
      setLoadingList(false);
    }
  }, [certificacionId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleBeforeUpload = async (file) => {
    if (!ALLOWED_MIMES.includes(file.type)) {
      message.error(`Tipo no soportado. Formatos aceptados: ${ALLOWED_LABEL}.`);
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_SIZE_BYTES) {
      message.error(`El archivo supera el máximo de ${MAX_SIZE_MB} MB.`);
      return Upload.LIST_IGNORE;
    }
    try {
      await upload(certificacionId, file);
      message.success(`"${file.name}" subido`);
      await refresh();
    } catch (err) {
      message.error(err?.message || 'Error subiendo el archivo');
    }
    // Retornamos false para que antd no dispare su propio request: el hook ya
    // hizo el PUT a S3 + confirm. LIST_IGNORE oculta el item del file list.
    return Upload.LIST_IGNORE;
  };

  const handleView = async (att) => {
    try {
      const { url } = await getCertificacionAttachmentUrl(certificacionId, att.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      message.error(err?.message || 'No se pudo abrir el archivo');
    }
  };

  const handleDelete = async (att) => {
    try {
      await deleteCertificacionAttachment(certificacionId, att.id);
      message.success('Adjunto eliminado');
      await refresh();
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'No se pudo eliminar');
    }
  };

  if (!certificacionId) return null;
  // MINVU off ⇒ los adjuntos de certificación no aplican para este tenant.
  if (!minvuEnabled) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: token.marginSM }}>
      <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
        <Text strong>Adjuntos</Text>
        {!readOnly && (
          <Upload
            multiple
            showUploadList={false}
            beforeUpload={handleBeforeUpload}
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          >
            <Button icon={<UploadOutlined />} loading={uploading} size="small">
              Subir archivo
            </Button>
          </Upload>
        )}
      </Space>

      {!readOnly && attachments.length === 0 && !loadingList && !loadError && (
        <Dragger
          multiple
          showUploadList={false}
          beforeUpload={handleBeforeUpload}
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          disabled={uploading}
          style={{ padding: token.paddingSM }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
            <InboxOutlined />
          </p>
          <p className="ant-upload-text" style={{ fontSize: 14 }}>
            Subí el certificado MINVU y anexos
          </p>
          <p className="ant-upload-hint" style={{ fontSize: 12 }}>
            {ALLOWED_LABEL} • máx {MAX_SIZE_MB} MB por archivo
          </p>
        </Dragger>
      )}

      {loadError && (
        <Alert
          type="error"
          showIcon
          message="No se pudieron cargar los adjuntos"
          description={loadError}
          action={
            <Button size="small" onClick={refresh}>
              Reintentar
            </Button>
          }
        />
      )}

      {loadingList ? (
        <Spin />
      ) : attachments.length > 0 ? (
        <List
          size="small"
          bordered
          dataSource={attachments}
          renderItem={(att) => (
            <List.Item
              actions={[
                <Tooltip key="view" title="Ver">
                  <Button size="small" icon={<EyeOutlined />} onClick={() => handleView(att)} />
                </Tooltip>,
                ...(readOnly
                  ? []
                  : [
                      <Popconfirm
                        key="delete"
                        title="Eliminar adjunto"
                        description="No se puede deshacer."
                        okText="Eliminar"
                        okType="danger"
                        cancelText="Cancelar"
                        onConfirm={() => handleDelete(att)}
                      >
                        <Tooltip title="Eliminar">
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                      </Popconfirm>,
                    ]),
              ]}
            >
              <List.Item.Meta
                avatar={iconFor(att.content_type, token.colorPrimary)}
                title={
                  <Space size="small" wrap>
                    <Text strong>{att.filename}</Text>
                    <Tag>{att.content_type}</Tag>
                  </Space>
                }
                description={
                  <Space size="small" wrap>
                    {att.size_bytes != null && (
                      <Text type="secondary">{formatSize(att.size_bytes)}</Text>
                    )}
                    <Text type="secondary">{dayjs(att.createdAt).format('DD/MM/YYYY HH:mm')}</Text>
                  </Space>
                }
              />
            </List.Item>
          )}
          locale={{
            emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Sin adjuntos" />,
          }}
        />
      ) : null}
    </div>
  );
}

export default CertificacionAttachmentsSection;
